import { useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { MealTemplate } from '../../lib/supabase'
import { sameNamed, TEMPLATE_NAME_MAX, templateSummary } from '../../lib/mealTemplates'
import { SheetPanel } from '../SheetPanel'

export interface TemplatesSheetProps {
  open: boolean
  /** "Week 42": the week on screen, which saving reads and using fills. */
  weekLabel: string
  /** Meals planned in that week (only those whose recipe still exists). */
  weekMealCount: number
  templates: MealTemplate[]
  recipeIds: Set<string>
  locked: boolean
  onSave: (name: string) => void
  /** Plans the template on the week on screen; returns how many meals it added. */
  onUse: (template: MealTemplate) => number
  onDelete: (id: string) => void
  onUpgrade: () => void
  onClose: () => void
}

// Week templates (036): save the week on screen under a name, put a saved one
// on the week on screen, or delete one. The body mounts only while open.
export function TemplatesSheet(props: TemplatesSheetProps) {
  return <AnimatePresence>{props.open && <Body {...props} />}</AnimatePresence>
}

function Body(p: TemplatesSheetProps) {
  const [name, setName] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const replaces = name.trim() ? sameNamed(p.templates, name) : undefined
  const guard = (fn: () => void) => () => (p.locked ? p.onUpgrade() : fn())

  function save() {
    if (!name.trim() || p.weekMealCount === 0) return
    p.onSave(name)
    setNote(`Saved ${p.weekLabel} as “${name.trim()}”`)
    setName('')
  }

  function use(t: MealTemplate) {
    const n = p.onUse(t)
    if (n === 0) { setNote(`${p.weekLabel} already has everything in “${t.name}”`); return }
    p.onClose()
  }

  return (
    <SheetPanel onClose={p.onClose} maxHeight="88dvh" layout="scroll" ariaLabel="Week templates" zIndex={12}>
      <div className="ml-templates">
        <header className="ml-templates__head">
          <h2 className="ml-display ml-templates__title">Week templates</h2>
          <button type="button" className="ml-btn ml-btn--secondary" onClick={p.onClose}>Done</button>
        </header>

        <section className="ml-templates__save" aria-label="Save as a template">
          <h3 className="ml-label">Save {p.weekLabel}</h3>
          {p.weekMealCount === 0 ? (
            <p className="ml-caption">Plan some meals this week first, then save it to repeat it.</p>
          ) : (
            <>
              <p className="ml-caption">
                {p.weekMealCount} {p.weekMealCount === 1 ? 'meal' : 'meals'}, on the same weekdays when you use it.
              </p>
              <div className="ml-templates__form">
                <input className="ml-input" aria-label="Template name" placeholder="Everyday week" maxLength={TEMPLATE_NAME_MAX}
                  value={name} onChange={e => { setName(e.target.value); setNote(null) }}
                  onKeyDown={e => { if (e.key === 'Enter') guard(save)() }} />
                <button type="button" className="ml-btn ml-btn--primary" disabled={!name.trim()} onClick={guard(save)}>
                  {replaces ? 'Replace' : 'Save'}
                </button>
              </div>
              {replaces && <p className="ml-caption">Replaces the template “{replaces.name}”.</p>}
            </>
          )}
        </section>

        {note && <p className="ml-templates__note" role="status">{note}</p>}

        {p.templates.length === 0 ? (
          <p className="ml-caption">No templates yet.</p>
        ) : (
          <ul className="ml-templates__list" aria-label="Templates">
            {p.templates.map(t => (
              <li key={t.id} className="ml-templates__row">
                <span className="ml-templates__text">
                  <span className="ml-templates__name">{t.name}</span>
                  <span className="ml-caption">{templateSummary(t, p.recipeIds)}</span>
                </span>
                {confirming === t.id ? (
                  <span className="ml-templates__actions">
                    <button type="button" className="ml-text-btn" onClick={() => setConfirming(null)}>Keep</button>
                    <button type="button" className="ml-text-btn ml-text-btn--destructive"
                      onClick={() => { p.onDelete(t.id); setConfirming(null) }}>Delete</button>
                  </span>
                ) : (
                  <span className="ml-templates__actions">
                    <button type="button" className="ml-btn ml-btn--secondary" aria-label={`Use ${t.name} for ${p.weekLabel}`}
                      onClick={guard(() => use(t))}>Use</button>
                    <button type="button" className="ml-text-btn ml-text-btn--destructive" aria-label={`Delete ${t.name}`}
                      onClick={guard(() => setConfirming(t.id))}>Delete</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </SheetPanel>
  )
}
